/**
 * MapOverlay — the large map toggled with M (gameState.mapOpen).
 *
 * create(hud) builds the PIXI objects into hud.elements.mapOverlay,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …)
 * redraws from game state. `hud` is the PixiHUDRenderer coordinator.
 *
 * The whole arena fit to the screen (letterboxed) over a translucent backdrop.
 * It is a see-through tactical view: gameplay continues underneath, so the
 * container ignores pointer events and never gates input. Drawing is shared
 * with the small Minimap via its exported routines; only the view differs.
 */
import * as PIXI from 'pixi.js';
import { changed, invalidate } from './dirty.js';
import { PixiCamera } from '../PixiCamera.js';
import { largeMapView, objectsKey, project } from './minimapMath.js';
import { minimapConfig, drawWorld, drawZone, drawMarkers } from './Minimap.js';

export function create(hud) {
  const container = new PIXI.Container();
  container.zIndex = 90; // below InventoryOverlay (100): Tab still draws on top of the map
  container.visible = false;
  container.eventMode = 'none'; // the canvas keeps receiving aim/shoot while the map is open

  const backdrop = new PIXI.Graphics();
  const objectsG = new PIXI.Graphics();
  const zoneG = new PIXI.Graphics();
  const viewportG = new PIXI.Graphics();
  const markersG = new PIXI.Graphics();
  const hint = hud._createText('Press M to close', 18, 0xffffff, true);
  hint.anchor.set(0, 0);
  hint.alpha = 0.85;

  container.addChild(backdrop, objectsG, zoneG, viewportG, markersG, hint);
  hud.hudContainer.addChild(container);
  hud.hudContainer.sortableChildren = true;

  hud.elements.mapOverlay = { container, backdrop, objectsG, zoneG, viewportG, markersG, hint };
  position(hud);
}

export function position(hud) {
  const el = hud.elements.mapOverlay;
  if (!el) return;

  el.backdrop.clear();
  el.backdrop.rect(0, 0, hud.screenWidth, hud.screenHeight);
  el.backdrop.fill({ color: 0x000000, alpha: 0.55 });

  // Top-left: the small minimap hides while the overlay is open, so its corner
  // is free, and the zone panel owns top-centre in battle royale.
  el.hint.x = 30;
  el.hint.y = 30;

  invalidate(el); // the fit changes with the screen size
}

export function update(hud, gameState, callbacks) {
  const el = hud.elements.mapOverlay;
  if (!el) return;

  const visible = Boolean(gameState.mapOpen);
  el.container.visible = visible;
  if (!visible) return;

  const cfg = minimapConfig(callbacks);
  const mapW = gameState.mapWidth || 0;
  const mapH = gameState.mapHeight || 0;
  const view = largeMapView(mapW, mapH, hud.screenWidth, hud.screenHeight, cfg.overlayInsets);

  const hintText = `Press ${String(cfg.toggleKey).toUpperCase()} to close`;
  if (el.hint.text !== hintText) el.hint.text = hintText;

  // tx/ty only change on resize, so this layer is drawn once per fit.
  if (changed(el, 'objects', { ...objectsKey(gameState.worldObjectsVersion, mapW, mapH, view.scale), tx: view.tx, ty: view.ty })) {
    el.objectsG.clear();
    drawWorld(el.objectsG, gameState.worldObjects, view, mapW, mapH);
  }

  // The interpolated view (GameState.zoneView()), so the full-screen map glides in step
  // with the world renderer and the small minimap instead of stepping once a second.
  const zs = typeof gameState.zoneView === 'function' ? gameState.zoneView() : gameState.zoneState;
  if (changed(el, 'zone', { shape: zs?.shape ?? null, target: zs?.targetShape ?? null, scale: view.scale, tx: view.tx, ty: view.ty })) {
    el.zoneG.clear();
    drawZone(el.zoneG, zs, view);
  }

  // What the player currently sees on screen, as a faint rectangle.
  el.viewportG.clear();
  if (PixiCamera.screenWidth) {
    const b = PixiCamera.getVisibleBounds();
    const tl = project(view, b.left, b.top);
    el.viewportG.rect(tl.x, tl.y, (b.right - b.left) * view.scale, (b.bottom - b.top) * view.scale);
    el.viewportG.stroke({ width: 1, color: 0xffffff, alpha: 0.35 });
  }

  el.markersG.clear();
  drawMarkers(el.markersG, gameState, view, performance.now(), cfg.shotMarkerMs);
}
